import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, canWrite, isSuperAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { BrushCleaning, Plus, RefreshCw, Search, ShieldAlert, Trash2, Wrench } from "lucide-react";

// BrushCleaning is available in current lucide-react and semantically fits Clean Sweep.
const SweepIcon = BrushCleaning || Wrench;

const DECISION_STYLE = {
  PENDING_REVIEW:      { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  RETAIN:              { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)" },
  CONSOLIDATE:         { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
  RETIRE:              { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" },
  ARCHIVE:             { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  EXCEPTION_APPROVED:  { bg: "hsl(150 45% 92%)", fg: "hsl(150 45% 24%)", border: "hsl(150 40% 66%)" },
};

function DecisionBadge({ value }) {
  const st = DECISION_STYLE[value] || DECISION_STYLE.PENDING_REVIEW;
  return <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ background: st.bg, color: st.fg, borderColor: st.border }}>{value}</Badge>;
}

function CreateFlagDialog({ vocab, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    flag_kind: "UNUSED_OR_LOW_USE", flag_summary: "",
    subject_field_name: "", subject_module_location: "",
    evidence_notes: "", revenue_protection_relevant: false,
    linked_registry_entry_id: "",
  });
  const submit = async () => {
    if (!form.flag_summary.trim()) { toast.error("Flag summary required."); return; }
    setSaving(true);
    try {
      const r = await api.post("/clean-sweep", form);
      onCreated?.(r.data);
      toast.success("Flag created.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="cs-new" className="gap-2"><Plus className="w-4 h-4" /> New flag</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Clean Sweep Flag</DialogTitle>
          <DialogDescription>Flag a metadata rule, tag, pathway, or requiredness setting that appears unused, redundant, obsolete, over-restrictive, or commercially harmful. Recording a flag does NOT modify production data.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Flag kind</Label>
            <Select value={form.flag_kind} onValueChange={(v)=>setForm({...form, flag_kind: v})}>
              <SelectTrigger data-testid="cs-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.flag_kinds || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Subject field name</Label><Input value={form.subject_field_name} onChange={(e)=>setForm({...form, subject_field_name: e.target.value})} data-testid="cs-field" /></div>
          <div><Label>Subject module / location</Label><Input value={form.subject_module_location} onChange={(e)=>setForm({...form, subject_module_location: e.target.value})} /></div>
          <div><Label>Linked registry entry id</Label><Input value={form.linked_registry_entry_id} onChange={(e)=>setForm({...form, linked_registry_entry_id: e.target.value})} /></div>
          <div className="col-span-2"><Label>Flag summary</Label><Textarea rows={2} value={form.flag_summary} onChange={(e)=>setForm({...form, flag_summary: e.target.value})} data-testid="cs-summary" /></div>
          <div className="col-span-2"><Label>Evidence notes</Label><Textarea rows={2} value={form.evidence_notes} onChange={(e)=>setForm({...form, evidence_notes: e.target.value})} /></div>
          <div className="col-span-2 flex items-center gap-2">
            <Checkbox id="cs-rev" checked={form.revenue_protection_relevant} onCheckedChange={(c)=>setForm({...form, revenue_protection_relevant: !!c})} data-testid="cs-revenue" />
            <Label htmlFor="cs-rev" className="text-sm">Revenue-protection relevant (blocks revenue, delivery, licensing, release, or catalogue progress)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cs-new-save">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ flag, vocab, onSaved }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState("RETAIN");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Decision reason is required."); return; }
    setSaving(true);
    try {
      const r = await api.post(`/clean-sweep/${flag.id}/decision`, { decision, decision_reason: reason.trim() });
      onSaved?.(r.data);
      toast.success("Decision recorded.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Decision failed.");
    } finally { setSaving(false); }
  };
  const choices = (vocab.decisions || []).filter((d) => d !== "PENDING_REVIEW");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`cs-decide-${flag.id}`}>Decide</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record decision</DialogTitle>
          <DialogDescription>Decisions do not destroy historical production data; retirements flow through the Metadata Registry lifecycle.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Decision</Label>
            <Select value={decision} onValueChange={setDecision}>
              <SelectTrigger data-testid="cs-decide-value"><SelectValue /></SelectTrigger>
              <SelectContent>{choices.map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason <span className="text-destructive">*</span></Label>
            <Textarea rows={3} value={reason} onChange={(e)=>setReason(e.target.value)} data-testid="cs-decide-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cs-decide-save">{saving ? "Saving…" : "Record decision"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CleanSweepDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [flagKind, setFlagKind] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [revenueOnly, setRevenueOnly] = useState(false);
  const [vocab, setVocab] = useState({});
  const mayWrite = canWrite(user);
  const canDecide = user && ["super_admin", "gfmp_reviewer"].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (flagKind) params.flag_kind = flagKind;
      if (decisionFilter) params.decision = decisionFilter;
      if (revenueOnly) params.revenue_only = "yes";
      const r = await api.get("/clean-sweep", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q, flagKind, decisionFilter, revenueOnly]);

  useEffect(() => {
    api.get("/clean-sweep/meta/vocab").then((r)=>setVocab(r.data || {})).catch(()=>{});
    load();
  }, [load]);

  const remove = async (id) => {
    if (!isSuperAdmin(user)) return;
    if (!window.confirm("Delete this Clean Sweep flag record?")) return;
    try { await api.delete(`/clean-sweep/${id}`); setRows((rs)=>rs.filter((r)=>r.id !== id)); toast.success("Deleted."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  if (!mayWrite) {
    return <div className="p-8 text-sm text-muted-foreground">Available only to authorised internal roles.</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="clean-sweep-desk">
      <div className="flex flex-wrap items-center gap-3">
        <SweepIcon className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Clean Sweep Protocol</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Identify metadata definitions, field rules, tags, pathways, or requiredness settings that are unused, redundant, obsolete, over-restrictive, or commercially harmful. Flags are for review — they never trigger destructive rewrites.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="cs-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        <CreateFlagDialog vocab={vocab} onCreated={(x)=>setRows((rs)=>[x, ...rs])} />
      </div>

      <Card style={{ background: "hsl(40 55% 97%)", borderColor: "hsl(40 55% 76%)" }}>
        <CardContent className="py-3 text-sm flex items-center gap-2" style={{ color: "hsl(35 60% 28%)" }}>
          <ShieldAlert className="w-4 h-4" />
          <span>{vocab.revenue_protection_policy || "No non-critical metadata field may block revenue, client delivery, licensing, release, or catalogue progress without a named legal / rights / accounting / delivery / compliance rationale."}</span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div><Label>Search</Label><Input value={q} onChange={(e)=>setQ(e.target.value)} data-testid="cs-filter-q" /></div>
            <div>
              <Label>Kind</Label>
              <Select value={flagKind || "__any__"} onValueChange={(v)=>setFlagKind(v === "__any__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.flag_kinds || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Decision</Label>
              <Select value={decisionFilter || "__any__"} onValueChange={(v)=>setDecisionFilter(v === "__any__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.decisions || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Checkbox id="cs-rev-filter" checked={revenueOnly} onCheckedChange={(c)=>setRevenueOnly(!!c)} data-testid="cs-filter-revenue" />
              <Label htmlFor="cs-rev-filter" className="text-sm">Revenue-protection only</Label>
            </div>
            <div className="flex items-end"><Button className="w-full" onClick={load} data-testid="cs-filter-apply">Apply</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Flags ({rows.length})</CardTitle>
          <CardDescription>Only Super Admin or GFMP Reviewer may record a decision. Every decision is audited.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
            rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="cs-empty">No flags recorded.</p> :
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="border border-border rounded-md px-4 py-3" data-testid={`cs-row-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.flag_kind}</Badge>
                    <DecisionBadge value={r.decision} />
                    {r.revenue_protection_relevant && (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]" style={{ background: "hsl(0 55% 94%)", color: "hsl(0 65% 30%)", borderColor: "hsl(0 55% 72%)" }}>Revenue protection</Badge>
                    )}
                    <span className="font-semibold text-sm flex-1">{r.flag_summary}</span>
                    {canDecide && r.decision === "PENDING_REVIEW" && (
                      <DecisionDialog flag={r} vocab={vocab} onSaved={(x)=>setRows((rs)=>rs.map((row)=>row.id===x.id ? x : row))} />
                    )}
                    {isSuperAdmin(user) && (
                      <InlineDeleteButton
                        endpoint={`/clean-sweep/${r.id}`}
                        recordLabel="Clean Sweep flag"
                        recordId={r.id}
                        isSuperAdmin={isSuperAdmin(user)}
                        variant="outline"
                        onDeleted={(id) => setRows((rs) => rs.filter((x) => x.id !== id))}
                        testid={`cs-delete-${r.id}`}
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                    <div><span className="opacity-60">Field:</span> {r.subject_field_name || "—"}</div>
                    <div><span className="opacity-60">Module:</span> {r.subject_module_location || "—"}</div>
                    <div><span className="opacity-60">Created by:</span> {r.created_by_email || "—"}</div>
                    <div><span className="opacity-60">Decided by:</span> {r.decision_reviewer || "—"}</div>
                  </div>
                  {r.evidence_notes && <div className="text-xs mt-2"><span className="opacity-60">Evidence:</span> {r.evidence_notes}</div>}
                  {r.decision !== "PENDING_REVIEW" && r.decision_reason && (
                    <div className="text-xs mt-2"><span className="opacity-60">Decision reason:</span> {r.decision_reason}</div>
                  )}
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}
